import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError, formatDate } from "../lib/api";
import { fmtMoney, fmtNumber, StatusPill, AgingChip } from "../components/ops/opsUtils";
import { ArrowLeft, Mail, Building2, Phone, Edit3, Save, X, User, Users, Move } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { MoveAttorneyDialog } from "../components/admin/MoveAttorneyDialog";
import { toast } from "sonner";

const EMPTY_EDIT = {
  name: "", firm: "", phone: "",
  primary_contact_name: "", primary_contact_email: "", primary_contact_phone: "",
  paralegal_name: "", paralegal_email: "", paralegal_phone: "",
  notes: "",
};

export default function AttorneyProfile() {
  const { email } = useParams();
  const emailDecoded = decodeURIComponent(email);
  const [profile, setProfile] = useState(null);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(EMPTY_EDIT);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [moveOpen, setMoveOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, l] = await Promise.all([
        api.get(`/operations/attorneys/${encodeURIComponent(emailDecoded)}`),
        api.get(`/operations/loans?attorney_email=${encodeURIComponent(emailDecoded)}&limit=5000`),
      ]);
      setProfile(p.data); setLoans(l.data);
      setDraft({
        name: p.data.name || "", firm: p.data.firm || "", phone: p.data.phone || "",
        primary_contact_name: p.data.primary_contact_name || "",
        primary_contact_email: p.data.primary_contact_email || "",
        primary_contact_phone: p.data.primary_contact_phone || "",
        paralegal_name: p.data.paralegal_name || "",
        paralegal_email: p.data.paralegal_email || "",
        paralegal_phone: p.data.paralegal_phone || "",
        notes: p.data.notes || "",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [emailDecoded]);

  const save = async () => {
    setSaving(true);
    try {
      // Only send non-empty fields so we don't blank existing values
      const payload = Object.fromEntries(Object.entries(draft).filter(([, v]) => v !== ""));
      await api.patch(`/operations/attorneys/${encodeURIComponent(emailDecoded)}`, payload);
      toast.success("Attorney profile updated");
      setEditing(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/operations/attorneys/${encodeURIComponent(emailDecoded)}/relationship-notes`, { content: note.trim() });
    setNote(""); toast.success("Relationship note added"); load();
  };

  if (loading) return <div className="p-10 text-[#6B7280]" data-testid="attorney-profile-loading">Loading…</div>;
  if (!profile) return <div className="p-10 text-[#6B7280]">Not found.</div>;

  const display = profile.name || emailDecoded;

  return (
    <div className="p-6 md:p-8 max-w-[1300px] mx-auto" data-testid="attorney-profile">
      <Link to="/admin/attorneys" className="inline-flex items-center text-sm text-[#6B7280] hover:text-[#0A2540] mb-3" data-testid="back-to-attorneys">
        <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> All attorneys
      </Link>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]" data-testid="atty-display-name">{display}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#6B7280] mt-2">
              <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {emailDecoded}</span>
              {profile.firm && <span className="inline-flex items-center gap-1.5" data-testid="atty-firm-line"><Building2 className="w-3.5 h-3.5" /> {profile.firm}</span>}
              {profile.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {profile.phone}</span>}
            </div>
          </div>
          {!editing ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setMoveOpen(true)} data-testid="move-attorney-btn">
                <Move className="w-3.5 h-3.5 mr-1.5" /> Move to firm
              </Button>
              <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-attorney-btn">
                <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Edit profile
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => { setEditing(false); load(); }} data-testid="cancel-edit-btn"><X className="w-3.5 h-3.5 mr-1.5" /> Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="save-attorney-btn"><Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}</Button>
            </div>
          )}
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mt-6 pt-5 border-t border-[#F3F4F6]">
          <Stat label="Funded volume" value={fmtMoney(profile.funded_volume)} accent />
          <Stat label="Active balance" value={fmtMoney(profile.active_balance)} />
          <Stat label="Active loans" value={fmtNumber(profile.active_count)} />
          <Stat label="Settled / Avg days" value={`${profile.settled_count}${profile.avg_settlement_days ? ` · ${profile.avg_settlement_days}d` : ""}`} />
        </div>

        {/* Inline editor */}
        {editing && (
          <div className="mt-6 pt-5 border-t border-[#F3F4F6] grid sm:grid-cols-2 gap-4" data-testid="attorney-edit-form">
            <div className="sm:col-span-2 text-xs uppercase tracking-wider text-[#6B7280] font-medium">Attorney</div>
            <Field label="Full name"><Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. Sergey Kats, Esq." data-testid="edit-name" /></Field>
            <Field label="Firm name"><Input value={draft.firm} onChange={(e) => setDraft((d) => ({ ...d, firm: e.target.value }))} placeholder="e.g. Kats Personal Injury Law" data-testid="edit-firm" /></Field>
            <Field label="Phone"><Input value={draft.phone} onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))} placeholder="(216) 555-0100" data-testid="edit-phone" /></Field>
            <div />

            <div className="sm:col-span-2 text-xs uppercase tracking-wider text-[#6B7280] font-medium mt-2">Primary contact</div>
            <Field label="Name"><Input value={draft.primary_contact_name} onChange={(e) => setDraft((d) => ({ ...d, primary_contact_name: e.target.value }))} data-testid="edit-primary-name" /></Field>
            <Field label="Email"><Input type="email" value={draft.primary_contact_email} onChange={(e) => setDraft((d) => ({ ...d, primary_contact_email: e.target.value }))} data-testid="edit-primary-email" /></Field>
            <Field label="Phone"><Input value={draft.primary_contact_phone} onChange={(e) => setDraft((d) => ({ ...d, primary_contact_phone: e.target.value }))} data-testid="edit-primary-phone" /></Field>
            <div />

            <div className="sm:col-span-2 text-xs uppercase tracking-wider text-[#6B7280] font-medium mt-2">Paralegal</div>
            <Field label="Name"><Input value={draft.paralegal_name} onChange={(e) => setDraft((d) => ({ ...d, paralegal_name: e.target.value }))} data-testid="edit-paralegal-name" /></Field>
            <Field label="Email"><Input type="email" value={draft.paralegal_email} onChange={(e) => setDraft((d) => ({ ...d, paralegal_email: e.target.value }))} data-testid="edit-paralegal-email" /></Field>
            <Field label="Phone"><Input value={draft.paralegal_phone} onChange={(e) => setDraft((d) => ({ ...d, paralegal_phone: e.target.value }))} data-testid="edit-paralegal-phone" /></Field>
            <div />

            <div className="sm:col-span-2 text-xs uppercase tracking-wider text-[#6B7280] font-medium mt-2">Internal relationship summary</div>
            <div className="sm:col-span-2"><Textarea rows={3} value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} placeholder="Anything the team should know about this firm — origin of relationship, preferences, deal context…" data-testid="edit-notes" /></div>
          </div>
        )}

        {/* Read-only contacts when not editing */}
        {!editing && (profile.primary_contact_name || profile.paralegal_name || profile.notes) && (
          <div className="mt-6 pt-5 border-t border-[#F3F4F6] grid sm:grid-cols-2 gap-5">
            {profile.primary_contact_name && (
              <ContactCard icon={User} title="Primary contact" name={profile.primary_contact_name} email={profile.primary_contact_email} phone={profile.primary_contact_phone} testid="primary-contact-card" />
            )}
            {profile.paralegal_name && (
              <ContactCard icon={Users} title="Paralegal" name={profile.paralegal_name} email={profile.paralegal_email} phone={profile.paralegal_phone} testid="paralegal-card" />
            )}
            {profile.notes && (
              <div className="sm:col-span-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-md p-4">
                <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Relationship summary</div>
                <div className="text-sm text-[#374151] whitespace-pre-wrap leading-relaxed">{profile.notes}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Relationship notes (append-only feed) */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-5" data-testid="relationship-notes-card">
        <div className="font-medium text-[#0A2540] mb-3">Relationship notes</div>
        <div className="flex gap-2 mb-3">
          <Textarea rows={2} placeholder="Met for coffee · Sent payoff letter · Discussed terms…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="add-relationship-note" />
          <Button onClick={addNote} className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="add-relationship-note-btn">Add</Button>
        </div>
        {(profile.relationship_notes || []).length === 0 ? (
          <div className="text-sm text-[#6B7280] italic">No notes yet.</div>
        ) : (
          <div className="space-y-2">
            {profile.relationship_notes.map((n) => (
              <div key={n.id} className="p-3 rounded-md bg-[#F9FAFB] border border-[#E5E7EB]" data-testid={`rel-note-${n.id}`}>
                <div className="text-xs text-[#6B7280] flex justify-between"><span className="font-medium text-[#0A2540]">{n.author}</span><span>{formatDate(n.created_at)}</span></div>
                <div className="text-sm text-[#374151] mt-1 whitespace-pre-wrap">{n.content}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Loan list */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-baseline justify-between">
          <div className="font-medium text-[#0A2540]">All loans</div>
          <span className="text-xs text-[#6B7280]">{loans.length} loans</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB]">
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
                <th className="px-4 py-2.5 font-medium">Client</th>
                <th className="px-4 py-2.5 font-medium">Funded</th>
                <th className="px-4 py-2.5 font-medium text-right">Principal</th>
                <th className="px-4 py-2.5 font-medium text-right">Current payoff</th>
                <th className="px-4 py-2.5 font-medium">Aging</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {loans.map((l, i) => (
                <tr key={l.loan_id} data-testid={`atty-loans-row-${i}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors">
                  <td className="px-4 py-2.5"><Link to={`/admin/loans/${l.loan_id}`} className="text-[#0A2540] font-medium hover:text-[#0066FF]">{l.client_name}</Link></td>
                  <td className="px-4 py-2.5 text-[#6B7280]">{formatDate(l.funding_date)}</td>
                  <td className="px-4 py-2.5 text-right text-[#0A2540] tabular-nums">{fmtMoney(l.principal)}</td>
                  <td className="px-4 py-2.5 text-right text-[#0A2540] tabular-nums">{fmtMoney(l.current_payoff)}</td>
                  <td className="px-4 py-2.5"><AgingChip bucket={l.aging_bucket} /></td>
                  <td className="px-4 py-2.5"><StatusPill status={l.current_status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <MoveAttorneyDialog
        open={moveOpen}
        onOpenChange={setMoveOpen}
        attorneyEmail={emailDecoded}
        currentFirmId={profile?.firm_id}
        onMoved={() => load()}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
function Stat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">{label}</div>
      <div className={`tabular-nums ${accent ? "text-[#0066FF] font-display text-2xl font-semibold" : "text-[#0A2540] text-lg font-medium"}`}>{value}</div>
    </div>
  );
}
function ContactCard({ icon: Icon, title, name, email, phone, testid }) {
  return (
    <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-md p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-2 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {title}</div>
      <div className="font-medium text-[#0A2540]">{name}</div>
      {email && <div className="text-xs text-[#6B7280] mt-0.5">{email}</div>}
      {phone && <div className="text-xs text-[#6B7280]">{phone}</div>}
    </div>
  );
}
